import React, { useMemo } from 'react';
import {
  GlobalSettingItemFragment,
  MediaImageFragment,
  PageCommunityNews
} from '@src/graphql/gql-types';
import Layout from '@src/components/Layout';
import SEO from '@src/components/SEO';
import { ContentfulPageHyperlinkFragments } from '@src/types/contentful';
import { getPageUrlPath } from '@src/utils/url';
import { Themes } from '@src/types/themes';
import {
  getFormEnquiryDisclaimerEntry,
  getSiteWideAlertBannerEntry
} from '@src/utils/globalSettings';
import { CommunityNewsPageProps } from '@src/types/page';
import {
  generateMetaTitle,
  getBreadcrumbs,
  getCommunityDetailsFromBreadcrumbs,
  getFooterNavItems,
  getOgImage
} from '@src/utils/helpers';
import HtmlComment from '@src/components/HtmlComment';
import { createReplaceDictionary } from '@src/utils/strings';
import NewsDetail from '@src/components/NewsDetail';
import HeroSmall from '@src/components/HeroSmall';
import { SITE_NAME } from '@src/constants/site';
import { useSetCommunityDetail } from '@src/hooks/useCommunityDetail';

type PageCommunityNewsTemplate = CommunityNewsPageProps;

const PageCommunityNewsTemplate: React.FC<PageCommunityNewsTemplate> = ({
  communityBasePagePayload,
  globalSettingsPayload,
  pagePayload
}) => {
  const { page, pagePath } = pagePayload || {};
  const pageData = page?.items[0];
  const globalSettingItems = globalSettingsPayload?.globalSettings
    ?.items as GlobalSettingItemFragment[];
  const pagePathData = pagePath?.items?.[0] as ContentfulPageHyperlinkFragments;
  const lookupPagesForCommunity = communityBasePagePayload
    ?.lookupPagesForCommunity?.items?.[0]?.linkedFrom
    ?.pageCommunityStandardCollection
    ?.items as ContentfulPageHyperlinkFragments[];

  const {
    communityDetail,
    footerNav,
    legalLinks,
    primaryNav: communityPrimaryNav,
    socialLinks
  } = communityBasePagePayload || {};

  const breadcrumbs = getBreadcrumbs(pagePathData, lookupPagesForCommunity);

  const formDisclaimerRichText =
    getFormEnquiryDisclaimerEntry(globalSettingItems);

  const title = pageData?.title;
  const seoTitle = pageData?.seoTitle;
  const seoDescription = pageData?.seoDescription;
  const noFollow = pageData?.noFollow;
  const noIndex = pageData?.noIndex;
  const community = pageData?.community;
  const bodyContent = pageData?.bodyContent;
  const hero = pageData?.hero;
  const introText = pageData?.introText;
  const downloadablePdFsCollection = pageData?.downloadablePdFsCollection;
  const relatedLinksCollection = pageData?.relatedLinksCollection;
  const readingTime = pageData?.readingTime;
  const publishedAt = pageData?.sys?.publishedAt;

  const alertBanner = getSiteWideAlertBannerEntry(globalSettingItems);

  // Theming
  const theme = communityDetail?.theme as Themes;
  const isLiveChatEnabled = !!community?.isLiveChatEnabled;
  const pageURLPath = getPageUrlPath(pagePathData);

  const communityDetailsSegment =
    getCommunityDetailsFromBreadcrumbs(breadcrumbs);

  const metaTitle =
    seoTitle ??
    generateMetaTitle([title, communityDetailsSegment?.title, SITE_NAME]);

  const metaOgImage = getOgImage(hero?.mediaItem as MediaImageFragment);

  // String replacement dictionary: gets passed through to richText.
  const replacementDictionary = createReplaceDictionary(
    globalSettingItems,
    publishedAt
  );

  const footerNavItems = getFooterNavItems(
    communityPrimaryNav,
    footerNav,
    socialLinks,
    legalLinks,
    breadcrumbs
  );

  const communityDetailData = useMemo(() => {
    const communityDetails = communityBasePagePayload?.communityDetail;

    return {
      slug: communityDetails?.slug ?? '',
      title: communityDetails?.title ?? '',
      state: (communityDetails?.state?.slug ?? '').toUpperCase()
    };
  }, [communityBasePagePayload]);

  // sets community details in context for use in lead forms
  useSetCommunityDetail(communityDetailData);

  return (
    <Layout
      alertBannerData={alertBanner}
      showBreadCrumbs
      pageCommunityData={pageData as PageCommunityNews}
      communityName={communityDetailsSegment?.title || pageData?.title}
      communitySlug={communityDetailsSegment?.slug}
      primaryNav={
        communityPrimaryNav?.items[0]?.navigationElementsCollection?.items
      }
      footerNav={footerNavItems}
      formDisclaimerRichText={formDisclaimerRichText}
      theme={theme}
      showLiveChat={isLiveChatEnabled}
    >
      <SEO
        metaTitle={metaTitle}
        metaDesc={seoDescription || ''}
        metaPageUrl={pageURLPath}
        metaOgImage={metaOgImage?.url}
        metaOgImageAlt={metaOgImage?.alt}
        noIndex={noIndex}
        noFollow={noFollow}
      />

      <HtmlComment comment="PageCommunityNews Template" />

      {hero && (
        <HeroSmall {...hero} replacementDictionary={replacementDictionary} />
      )}

      <NewsDetail
        readingTime={readingTime}
        introText={introText}
        relatedLinksCollection={relatedLinksCollection}
        bodyContent={bodyContent}
        downloadablePdFsCollection={downloadablePdFsCollection}
        replacementDictionary={replacementDictionary}
      />
    </Layout>
  );
};

export default PageCommunityNewsTemplate;
